"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var changeElementTag_1 = require("../utils/changeElementTag");
var getInheritableStyles_1 = require("./getInheritableStyles");
var getPredefinedCssForElement_1 = require("./getPredefinedCssForElement");
var getStyles_1 = require("../style/getStyles");
var getTagOfNode_1 = require("../utils/getTagOfNode");
var safeInstanceOf_1 = require("../utils/safeInstanceOf");
var setStyles_1 = require("../style/setStyles");
var toArray_1 = require("../utils/toArray");
var cloneObject_1 = require("./cloneObject");
var getAllowedValues_1 = require("./getAllowedValues");
/**
 * HTML sanitizer class provides two features:
 * 1. Convert global CSS to inline CSS
 * 2. Sanitize an HTML document, remove unnecessary/dangerous attribute/nodes
 */
var HtmlSanitizer = /** @class */ (function () {
    /**
     * Construct a new instance of HtmlSanitizer
     * @param options Options for HtmlSanitizer
     */
    function HtmlSanitizer(options) {
        options = options || {};
        this.elementCallbacks = cloneObject_1.cloneObject(options.elementCallbacks);
        this.styleCallbacks = getAllowedValues_1.getStyleCallbacks(options.cssStyleCallbacks);
        this.attributeCallbacks = cloneObject_1.cloneObject(options.attributeCallbacks);
        this.tagReplacements = getAllowedValues_1.getTagReplacement(options.additionalTagReplacements);
        this.allowedAttributes = getAllowedValues_1.getAllowedAttributes(options.additionalAllowedAttributes);
        this.allowedCssClassesRegex = getAllowedValues_1.getAllowedCssClassesRegex(options.additionalAllowedCssClasses);
        this.defaultStyleValues = getAllowedValues_1.getDefaultStyleValues(options.additionalDefaultStyleValues);
        this.additionalPredefinedCssForElement = options.additionalPredefinedCssForElement;
        this.additionalGlobalStyleNodes = options.additionalGlobalStyleNodes || [];
        this.preserveHtmlComments = options.preserveHtmlComments;
        this.unknownTagReplacement = options.unknownTagReplacement;
    }
    /**
     * Convert global CSS to inline CSS if any
     * @param html HTML source
     * @param additionalStyleNodes (Optional) additional HTML STYLE elements used as global CSS
     */
    HtmlSanitizer.convertInlineCss = function (html, additionalStyleNodes) {
        var sanitizer = new HtmlSanitizer({
            additionalGlobalStyleNodes: additionalStyleNodes,
        });
        return sanitizer.exec(html, true /*convertCssOnly*/);
    };
    /**
     * Sanitize HTML string, remove any unused HTML node/attribute/CSS.
     * @param html HTML source string
     * @param options Options used for this sanitizing process
     */
    HtmlSanitizer.sanitizeHtml = function (html, options) {
        options = options || {};
        var sanitizer = new HtmlSanitizer(options);
        var currentStyles = safeInstanceOf_1.default(options.currentElementOrStyle, 'HTMLElement')
            ? getInheritableStyles_1.default(options.currentElementOrStyle)
            : options.currentElementOrStyle;
        return sanitizer.exec(html, options.convertCssOnly, currentStyles);
    };
    /**
     * Sanitize HTML string
     * This function will do the following work:
     * 1. Convert global CSS into inline CSS
     * 2. Remove dangerous HTML tags and attributes
     * 3. Remove useless CSS properties
     * @param html The input HTML
     * @param convertInlineCssOnly Whether only convert inline css and skip html content sanitizing
     * @param currentStyles Current inheritable CSS styles
     */
    HtmlSanitizer.prototype.exec = function (html, convertCssOnly, currentStyles) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(unsafeConvertToTrustedHTML(html) || '', 'text/html');
        if (doc && doc.body && doc.body.firstChild) {
            this.convertGlobalCssToInlineCss(doc);
            if (!convertCssOnly) {
                this.sanitize(doc.body, currentStyles);
            }
        }
        return (doc && doc.body && doc.body.innerHTML) || '';
    };
    /**
     * Sanitize an HTML element, remove unnecessary or dangerous elements/attribute/CSS rules
     * @param rootNode Root node to sanitize
     * @param currentStyles Current CSS styles. Inheritable styles in the given node which has
     * the same value with current styles will be ignored.
     */
    HtmlSanitizer.prototype.sanitize = function (rootNode, currentStyles) {
        if (!rootNode) {
            return '';
        }
        currentStyles = cloneObject_1.cloneObject(currentStyles, getInheritableStyles_1.default(null));
        this.processNode(rootNode, currentStyles, {});
    };
    /**
     * Convert global CSS into inline CSS
     * @param rootNode The HTML Document
     */
    HtmlSanitizer.prototype.convertGlobalCssToInlineCss = function (rootNode) {
        var styleNodes = toArray_1.default(rootNode.querySelectorAll('style'));
        var styleSheets = this.additionalGlobalStyleNodes
            .reverse()
            .map(function (node) { return node.sheet; })
            .concat(styleNodes.map(function (node) { return node.sheet; }).reverse())
            .filter(function (sheet) { return sheet; });
        for (var _i = 0, styleSheets_1 = styleSheets; _i < styleSheets_1.length; _i++) {
            var styleSheet = styleSheets_1[_i];
            var _loop_1 = function (j) {
                // Skip any none-style rule, i.e. @page
                var styleRule = styleSheet.cssRules[j];
                var text = styleRule && styleRule.style ? styleRule.style.cssText : null;
                if (styleRule.type != CSSRule.STYLE_RULE || !text || !styleRule.selectorText) {
                    return "continue";
                }
                // Make sure the selector is not empty
                for (var _i = 0, _a = styleRule.selectorText.split(','); _i < _a.length; _i++) {
                    var selector = _a[_i];
                    if (!selector || !selector.trim() || selector.indexOf(':') >= 0) {
                        continue;
                    }
                    var nodes = toArray_1.default(rootNode.querySelectorAll(selector));
                    // Always put existing styles after so that they have higher priority
                    // Which means if both global style and inline style apply to the same element,
                    // inline style will have higher priority
                    nodes.forEach(function (node) {
                        return node.setAttribute('style', text + (node.getAttribute('style') || ''));
                    });
                }
            };
            for (var j = styleSheet.cssRules.length - 1; j >= 0; j--) {
                _loop_1(j);
            }
        }
        styleNodes.forEach(function (node) {
            if (node.parentNode) {
                node.parentNode.removeChild(node);
            }
        });
    };
    HtmlSanitizer.prototype.processNode = function (node, currentStyle, context) {
        var nodeType = node.nodeType;
        var isElement = nodeType == 1 /* Element */;
        var isText = nodeType == 3 /* Text */;
        var isFragment = nodeType == 11 /* DocumentFragment */;
        var isComment = nodeType == 8 /* Comment */;
        var shouldKeep = false;
        if (isElement) {
            var tag = getTagOfNode_1.default(node);
            var callback = this.elementCallbacks[tag];
            var replacement = this.tagReplacements[tag.toLowerCase()];
            if (replacement === undefined) {
                replacement = this.unknownTagReplacement;
            }
            if (callback) {
                shouldKeep = callback(node, context);
            }
            else if (tag.indexOf(':') > 0) {
                shouldKeep = true;
            }
            else if (tag == replacement || replacement == '*') {
                shouldKeep = true;
            }
            else if (replacement && /^[a-zA-Z][\w\-]*$/.test(replacement)) {
                node = changeElementTag_1.default(node, replacement);
                shouldKeep = true;
            }
        }
        else if (isText) {
            var whiteSpace = currentStyle['white-space'];
            shouldKeep =
                whiteSpace == 'pre' ||
                    whiteSpace == 'pre-line' ||
                    whiteSpace == 'pre-wrap' ||
                    !/^[\r\n]*$/g.test(node.nodeValue);
        }
        else if (isFragment) {
            shouldKeep = true;
        }
        else if (isComment) {
            shouldKeep = this.preserveHtmlComments;
        }
        else {
            shouldKeep = false;
        }
        if (!shouldKeep) {
            node.parentNode.removeChild(node);
        }
        else if (isText &&
            (currentStyle['white-space'] == 'pre' || currentStyle['white-space'] == 'pre-wrap')) {
            node.nodeValue = node.nodeValue.replace(/^ /gm, '\u00A0').replace(/ {2}/g, ' \u00A0');
        }
        else if (isElement || isFragment) {
            var thisStyle = cloneObject_1.cloneObject(currentStyle);
            var element = node;
            if (isElement) {
                this.processAttributes(element, context);
                this.preprocessCss(element, thisStyle);
                this.processCss(element, thisStyle, context);
            }
            var child = element.firstChild;
            var next = void 0;
            for (; child; child = next) {
                next = child.nextSibling;
                this.processNode(child, thisStyle, context);
            }
        }
    };
    HtmlSanitizer.prototype.preprocessCss = function (element, thisStyle) {
        var predefinedStyles = getPredefinedCssForElement_1.default(element, this.additionalPredefinedCssForElement);
        if (predefinedStyles) {
            Object.keys(predefinedStyles).forEach(function (name) {
                thisStyle[name] = predefinedStyles[name];
            });
        }
    };
    HtmlSanitizer.prototype.processCss = function (element, thisStyle, context) {
        var _this = this;
        var styles = getStyles_1.default(element);
        Object.keys(styles).forEach(function (name) {
            var value = styles[name];
            var callback = _this.styleCallbacks[name];
            var isInheritable = thisStyle[name] != undefined;
            var keep = (!callback || callback(value, element, thisStyle, context)) &&
                value != 'inherit' &&
                value.indexOf('expression') < 0 &&
                name.substr(0, 1) != '-' &&
                _this.defaultStyleValues[name] != value &&
                ((isInheritable && value != thisStyle[name]) ||
                    (!isInheritable && value != 'initial' && value != 'normal'));
            if (keep && isInheritable) {
                thisStyle[name] = value;
            }
            if (!keep) {
                delete styles[name];
            }
        });
        setStyles_1.default(element, styles);
    };
    HtmlSanitizer.prototype.processAttributes = function (element, context) {
        for (var i = element.attributes.length - 1; i >= 0; i--) {
            var attribute = element.attributes[i];
            var name_1 = attribute.name.toLowerCase().trim();
            var value = attribute.value;
            var callback = this.attributeCallbacks[name_1];
            var newValue = callback
                ? callback(value, element, context)
                : this.allowedAttributes.indexOf(name_1) >= 0 || name_1.indexOf('data-') == 0
                    ? value
                    : null;
            if (name_1 == 'class' && this.allowedCssClassesRegex) {
                newValue = this.processCssClass(value, newValue);
            }
            if (newValue === null ||
                newValue === undefined ||
                newValue.match(/s\n*c\n*r\n*i\n*p\n*t\n*:/i) // match script: with any NewLine inside. Browser will ignore those NewLine char and still treat it as script prefix
            ) {
                element.removeAttribute(name_1);
            }
            else {
                attribute.value = newValue;
            }
        }
    };
    HtmlSanitizer.prototype.processCssClass = function (originalValue, calculatedValue) {
        var _this = this;
        var originalClasses = originalValue ? originalValue.split(' ') : [];
        var calculatedClasses = calculatedValue ? calculatedValue.split(' ') : [];
        originalClasses.forEach(function (className) {
            if (_this.allowedCssClassesRegex.test(className) &&
                calculatedClasses.indexOf(className) < 0) {
                calculatedClasses.push(className);
            }
        });
        return calculatedClasses.length > 0 ? calculatedClasses.join(' ') : null;
    };
    return HtmlSanitizer;
}());
exports.default = HtmlSanitizer;
var trustedTypes = window.trustedTypes;
var policy = trustedTypes === null || trustedTypes === void 0 ? void 0 : trustedTypes.createPolicy('roosterjsUnsafeConvertHTML', {
    // This is unsafe. However, we only use this function for HtmlSanitizer which we will
    // sanitize HTML tree by our own code. So we just directly return the HTML string here.
    createHTML: function (html) { return html; },
});
var unsafeConvertToTrustedHTML = policy
    ? function (html) { return policy.createHTML(html || ''); }
    : function (html) { return html; };
//# sourceMappingURL=HtmlSanitizer.js.map